import { FC } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Typography } from '@mui/material';
import { updateUserAttribute } from 'aws-amplify/auth';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { IStepFormProps as Props } from 'interfaces/auth';

import { Button } from 'components/_basic/Button/Button';
import { CloseButton } from 'components/_basic/CloseButton/CloseButton';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { useChangeEmailContext } from 'components/ChangeEmail/ChangeEmailContextProvider';

import ToastService from 'helpers/ToastService';
import { changeEmailSchema } from 'helpers/validations/changeEmailFormValidation';

import { IEnterNewEmailForm } from '../types';

import styles from './EnterNewEmailStep.module.scss';

export const EnterNewEmailStep: FC<Props> = ({ onClose }) => {
  const { nextStep, newEmail, setNewEmail } = useChangeEmailContext();
  const { t } = useTranslation();
  const { track } = usePromiseStatus();

  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<IEnterNewEmailForm>({
    defaultValues: {
      email: newEmail,
    },
    resolver: yupResolver<IEnterNewEmailForm>(changeEmailSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const closeModalHandler = () => {
    setNewEmail('');
    onClose();
  };

  const enterNewEmailSubmitHandler: SubmitHandler<IEnterNewEmailForm> = async (data): Promise<void> => {
    try {
      await track(
        updateUserAttribute({
          userAttribute: {
            attributeKey: 'email',
            value: data.email,
          },
        }),
      );

      setNewEmail(data.email);
      nextStep();
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  };

  return (
    <Box className={styles.enterEmailWrapper}>
      <Grid container alignItems="center">
        <Grid item xs={11}>
          <Typography variant="h1" className={styles.formTitle}>
            {t('changeEmail')}
          </Typography>
        </Grid>
        <Grid item xs={1}>
          <CloseButton onClick={closeModalHandler} />
        </Grid>
      </Grid>
      <Typography variant="subtitle1">{t('enterYourNewEmailToGetConfirmationCode')}</Typography>
      <Box onSubmit={handleSubmit(enterNewEmailSubmitHandler)} component="form" className={styles.formWrapper}>
        <TextInputControl name="email" label={t('email')} placeholder="user@mail.com" control={control} />
        <Button fullWidth disabled={!isValid} type="submit" variant="contained">
          {t('continue')}
        </Button>
      </Box>
    </Box>
  );
};
